import { Add, Chat, Delete, Edit, TableChart, TableRows } from '@mui/icons-material';
import
    {
        Box,
        Checkbox,
        Chip,
        Paper,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TablePagination,
        TableRow,
        Typography,
    } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, ELinks } from '@sharedInterfaces/globalEnums';
import { EEmployeeTalkStatus } from '@sharedInterfaces/IEmployeeTalk';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { getEmployeeTalks } from '@src/APIs/graphQl/EmployeeTalk';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { EmployeeTalkDialog } from '@src/App/Components/Dialogs/EmployeeTalkDialog/EmployeeTalkDialog';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import type { IDataTableField } from '@src/App/Components/Tables/DataTable/DataTable';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type EmployeeTalk from '@src/Objects/EmployeeTalk';
import { textContainsLowerCase } from '@src/Objects/Helper';
import { setEmployeeTalks } from '@store/reducer/employeeTalksReducer';
import type { AppState } from '@store/store';
import { MessagesSquare } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import EntitiesPage, { EViewType, type IEntitiesPageAction } from '../../Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '../../Generic/EntitiesPage/ViewTypeMenu';

/**
 * MvpEmployeeTalksPage component with modern MUI design using EntitiesPage system.
 *
 * @returns {JSX.Element} The JSX element for the MvpEmployeeTalksPage.
 */
export default function MvpEmployeeTalksPage()
{
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const emp = useSelector((state: AppState) => state.employee);
    const permissions = useSelector((state: AppState) => state.permissions);
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const employeeTalks = useSelector((state: AppState) => state.employeeTalks);

    const [loading, setLoading] = useState<boolean>(true);
    const [selected, setSelected] = useState<number[]>([]);
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [activeRole, setActiveRole] = useState('employee');

    useEffect(() =>
    {
        const canRetrieve = checkPermissions('EmployeeTalks', 'Retrieve', permissions);
        const canRetrieveOwn = checkPermissions('EmployeeTalks', 'RetrieveOwn', permissions);

        if (!canRetrieve && !canRetrieveOwn)
        {
            setLoadingError({ statusCode: 403, message: langStrings.errorLoadingEmployeeTalk });
            setLoading(false);
            return;
        }

        getEmployeeTalks()
            .then(() =>
            {
                setLoading(false);
            })
            .catch((ex) =>
            {
                setLoadingError(ex);
                setLoading(false);
            });
    }, [permissions, langStrings.errorLoadingEmployeeTalk]);

    const getStatusChipColor = (status2: EEmployeeTalkStatus) =>
    {
        switch (status2)
        {
            case EEmployeeTalkStatus.PLANNED:
                return 'primary';
            case EEmployeeTalkStatus.COMPLETED:
                return 'success';
            case EEmployeeTalkStatus.CANCELLED:
                return 'error';
            case EEmployeeTalkStatus.POSTPONED:
                return 'warning';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString?: string) =>
    {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const handleCreateEmployeeTalk = () =>
    {
        openDialog(
            <EmployeeTalkDialog
                id="createEmployeeTalkDialog"
                resolve={(newEmployeeTalk) =>
                {
                    if (newEmployeeTalk)
                    {
                        dispatch(setEmployeeTalks([...employeeTalks, newEmployeeTalk]));
                    }
                }}
            />,
        );
    };

    const handleViewEmployeeTalk = (id: number) =>
    {
        navigate(`/${ELinks.EMPLOYEE_TALKS}/${id}`);
    };

    const handleRowDoubleClick = (id: number) =>
    {
        handleViewEmployeeTalk(id);
    };

    const getEmployeeName = (employeeId: number) =>
    {
        const employee = allEmployees.find((e) => e.id === employeeId);
        return employee ? employee.title : `ID: ${employeeId}`;
    };

    const renderEmployeePicture = (employeeId: number) =>
    {
        const employee = allEmployees.find((e) => e.id === employeeId);
        if (!employee) return null;

        const parts = (employee.title ?? '').trim().split(' ');
        const firstName = parts[0] || '';
        const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

        return (
            <EmployeePicture
                employee={{
                    id: employee.id,
                    firstName,
                    lastName,
                    pictureId: employee.pictureId || null,
                }}
                avatarSize={32}
            />
        );
    };

    const types: IViewType<EmployeeTalk>[] = [
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (allEntities, innerEntries, width, selectedItems, tableSettings) =>
            {
                if (!tableSettings) return <div>Loading...</div>;

                const hiddenFieldsSet = new Set(
                    Object.keys(tableSettings.hiddenFields).filter((key) => tableSettings.hiddenFields[key]),
                );
                const visibleFields = tableFields.filter((f) => !hiddenFieldsSet.has(f.id));
                const sortedFields = visibleFields.sort((a, b) =>
                {
                    const orderA = tableSettings.order.indexOf(a.id);
                    const orderB = tableSettings.order.indexOf(b.id);
                    return orderA - orderB;
                });

                return (
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750 }}>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    sx={{
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                        '&.Mui-checked': {
                                                            color: (theme) =>
                                                                theme.palette.mode === 'dark'
                                                                    ? theme.palette.primary.light
                                                                    : theme.palette.primary.dark,
                                                        },
                                                    }}
                                                    indeterminate={
                                                        selected.length > 0 && selected.length < innerEntries.length
                                                    }
                                                    checked={
                                                        innerEntries.length > 0 &&
                                                        selected.length === innerEntries.length
                                                    }
                                                    onChange={(event) =>
                                                    {
                                                        if (event.target.checked)
                                                        {
                                                            setSelected(innerEntries.map((n) => n.id));
                                                        } else
                                                        {
                                                            setSelected([]);
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            {sortedFields.map((field) => (
                                                <TableCell
                                                    key={field.id}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((entity) =>
                                        {
                                            const isItemSelected = selected.includes(entity.id);
                                            return (
                                                <TableRow
                                                    hover
                                                    onClick={() =>
                                                    {
                                                        const selectedIndex = selected.indexOf(entity.id);
                                                        let newSelected: number[] = [];
                                                        if (selectedIndex === -1)
                                                        {
                                                            newSelected = newSelected.concat(selected, entity.id);
                                                        } else if (selectedIndex === 0)
                                                        {
                                                            newSelected = newSelected.concat(selected.slice(1));
                                                        } else if (selectedIndex === selected.length - 1)
                                                        {
                                                            newSelected = newSelected.concat(selected.slice(0, -1));
                                                        } else if (selectedIndex > 0)
                                                        {
                                                            newSelected = newSelected.concat(
                                                                selected.slice(0, selectedIndex),
                                                                selected.slice(selectedIndex + 1),
                                                            );
                                                        }
                                                        setSelected(newSelected);
                                                    }}
                                                    onDoubleClick={() => handleRowDoubleClick(entity.id)}
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={entity.id}
                                                    selected={isItemSelected}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox color="primary" checked={isItemSelected} />
                                                    </TableCell>
                                                    {sortedFields.map((field) => (
                                                        <TableCell key={field.id}>
                                                            {field.id === 'id' && entity.id}
                                                            {field.id === 'title' && entity.title}
                                                            {field.id === 'employee' && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    {renderEmployeePicture(entity.employeeId)}
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 500 }}
                                                                    >
                                                                        {getEmployeeName(entity.employeeId)}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {field.id === 'manager' && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    {renderEmployeePicture(entity.meetingPartnerId)}
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 500 }}
                                                                    >
                                                                        {getEmployeeName(entity.meetingPartnerId)}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {field.id === 'template' && entity.templateId}
                                                            {field.id === 'scheduledDate' &&
                                                                formatDate(entity.scheduledDate)}
                                                            {field.id === 'status' && (
                                                                <Chip
                                                                    label={entity.status2}
                                                                    color={getStatusChipColor(entity.status2)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities, innerEntries, width, selectedItems, tableSettings) => (
                <Container>
                    {innerEntries.map((entity) =>
                    {
                        const isSelected = selected.includes(entity.id);
                        return (
                            <Paper
                                key={entity.id}
                                sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    mb: 2,
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: isSelected ? 'none' : '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: isSelected
                                        ? (theme) => theme.palette.primary.main + '1A'
                                        : 'background.paper',
                                }}
                                onClick={(e) =>
                                {
                                    if (
                                        e.target === e.currentTarget ||
                                        !(e.target as HTMLElement).closest('input[type="checkbox"]')
                                    )
                                    {
                                        handleViewEmployeeTalk(entity.id);
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        background: (theme) =>
                                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                        color: 'primary.contrastText',
                                        p: 2,
                                        m: -2,
                                        mb: 1,
                                        borderRadius: '8px 8px 0 0',
                                    }}
                                >
                                    {actions.length > 0 && (
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={(e) =>
                                            {
                                                e.stopPropagation();
                                                const newSelected = isSelected
                                                    ? selected.filter((id) => id !== entity.id)
                                                    : [...selected, entity.id];
                                                setSelected(newSelected);
                                            }}
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                '&.Mui-checked': { color: 'white' },
                                                p: 0.5,
                                                mr: 1,
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flexGrow: 1 }}>
                                        {(() =>
                                        {
                                            const parts = entity.title.split(' - ');
                                            if (parts.length >= 2)
                                            {
                                                const templateName = parts[0];
                                                const employeeName = parts.slice(1).join(' - ');
                                                return (
                                                    <>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ fontWeight: 600, color: 'inherit', lineHeight: 1.2 }}
                                                        >
                                                            {templateName}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 400,
                                                                color: 'rgba(255,255,255,0.8)',
                                                                fontSize: '0.85em',
                                                            }}
                                                        >
                                                            {employeeName}
                                                        </Typography>
                                                    </>
                                                );
                                            }
                                            return (
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{ fontWeight: 600, color: 'inherit' }}
                                                >
                                                    {entity.title}
                                                </Typography>
                                            );
                                        })()}
                                    </Box>
                                    <Chip
                                        label={entity.status2}
                                        color={getStatusChipColor(entity.status2)}
                                        size="small"
                                        variant="filled"
                                        sx={{ color: 'white' }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <strong>{langStrings.oftenUsed.employee}:</strong>
                                    {renderEmployeePicture(entity.employeeId)}
                                    <span>{getEmployeeName(entity.employeeId)}</span>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <strong>{langStrings.manager}:</strong>
                                    {renderEmployeePicture(entity.meetingPartnerId)}
                                    <span>{getEmployeeName(entity.meetingPartnerId)}</span>
                                </Box>
                                <div>
                                    <strong>{langStrings.date}:</strong> {formatDate(entity.scheduledDate)}
                                </div>
                                <div>
                                    <strong>{langStrings.template}:</strong> {entity.templateId}
                                </div>
                            </Paper>
                        );
                    })}
                </Container>
            ),
        },
    ];

    const handleEditEmployeeTalk = (id: number) =>
    {
        const employeeTalkToEdit = employeeTalks.find((talk) => talk.id === id);
        if (employeeTalkToEdit)
        {
            openDialog(
                <EmployeeTalkDialog
                    id="editEmployeeTalkDialog"
                    employeeTalk={employeeTalkToEdit}
                    disableEmployeeAndTemplate={true}
                    resolve={(updatedEmployeeTalk) =>
                    {
                        if (updatedEmployeeTalk)
                        {
                            const updatedTalks = employeeTalks.map((talk) =>
                                talk.id === updatedEmployeeTalk.id ? updatedEmployeeTalk : talk,
                            );
                            dispatch(setEmployeeTalks(updatedTalks));
                        }
                    }}
                />,
            );
        }
    };

    const handleDeleteEmployeeTalk = async (id: number) =>
    {
        const employeeTalkToDelete = employeeTalks.find((talk) => talk.id === id);
        if (employeeTalkToDelete)
        {
            try
            {
                await employeeTalkToDelete.delete();
            } catch (error)
            {
                console.error('Error deleting employee talk:', error);
            }
        }
    };

    const actions: IEntitiesPageAction[] = [
        {
            id: 'create',
            text: langStrings.new,
            icon: <Add />,
            filter()
            {
                return checkPermissions('EmployeeTalks', 'Add', permissions);
            },
            action()
            {
                handleCreateEmployeeTalk();
            },
        },
        {
            id: 'edit',
            text: langStrings.oftenUsed.edit,
            icon: <Edit />,
            filter()
            {
                return selected.length === 1 && checkPermissions('EmployeeTalks', 'Edit', permissions);
            },
            action()
            {
                if (selected.length === 1)
                {
                    handleEditEmployeeTalk(selected[0]);
                }
            },
        },
        {
            id: 'delete',
            text: langStrings.oftenUsed.delete,
            icon: <Delete />,
            filter()
            {
                return selected.length > 0 && checkPermissions('EmployeeTalks', 'Delete', permissions);
            },
            action()
            {
                selected.forEach((id) => handleDeleteEmployeeTalk(id));
                setSelected([]);
            },
        },
    ];

    const tableFields: IDataTableField<any>[] = [
        { id: 'id', label: 'ID', align: 'left', disablePadding: false, type: 'string' },
        { id: 'title', label: langStrings.title, align: 'left', disablePadding: false, type: 'string' },
        { id: 'employee', label: langStrings.oftenUsed.employee, align: 'left', disablePadding: false, type: 'string' },
        { id: 'manager', label: langStrings.manager, align: 'left', disablePadding: false, type: 'string' },
        { id: 'template', label: langStrings.template, align: 'left', disablePadding: false, type: 'string' },
        { id: 'scheduledDate', label: langStrings.date, align: 'left', disablePadding: false, type: 'string' },
        { id: 'status', label: langStrings.status, align: 'left', disablePadding: false, type: 'string' },
    ];

    const roles: IMvpRole[] = [
        {
            id: 'employee',
            label: 'Mitarbeitende',
            icon: getAppIcon(EIconCategory.PROFILE),
        },
        {
            id: 'manager',
            label: 'Führungskraft',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
    ];

    const getQuickActionsForRole = (role: string): IMvpQuickAction[] =>
    {
        const canCreate = checkPermissions('EmployeeTalks', 'Add', permissions);
        const canManageTemplates = checkPermissions('EmployeeTalkTemplates', 'Edit', permissions);

        switch (role)
        {
            case 'employee': {
                return canCreate
                    ? [
                        {
                            id: 'newTalk',
                            label: 'Neues Gespräch',
                            icon: <MessagesSquare size={16} />,
                            onClick: handleCreateEmployeeTalk,
                            variant: 'primary',
                        },
                    ]
                    : [];
            }

            case 'manager':
            case 'admin': {
                const actions: IMvpQuickAction[] = [];

                if (canCreate)
                {
                    actions.push({
                        id: 'newTalk',
                        label: 'Neues Gespräch',
                        icon: <MessagesSquare size={16} />,
                        onClick: handleCreateEmployeeTalk,
                        variant: 'primary',
                    });
                }

                if (canManageTemplates)
                {
                    actions.push({
                        id: 'templates',
                        label: 'Vorlagen bearbeiten',
                        icon: <Edit />,
                        onClick: () => navigate(`/${ELinks.SETTINGS_EMPLOYEE_TALK_TEMPLATE}`),
                    });
                }

                return actions;
            }

            default:
                return [];
        }
    };

    if (loading) return <LoadingBox />;

    return (
        <>
            <MvpSubNav
                currentPageTitle="T4lo Talks"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={getQuickActionsForRole(activeRole)}
            />
            <EntitiesPage
                title="T4lo Talks"
                subTitle="Verwalten Sie alle Mitarbeitergespräche und deren Status"
                entities={employeeTalks}
                selected={selected}
                loadingError={loadingError as any}
                tableFields={tableFields}
                tableId="employee-talks-table"
                filter={(entity, selectedView, searchText) =>
                    selectedView.filter(entity) &&
                    (textContainsLowerCase(searchText, entity.id.toString()) ||
                        textContainsLowerCase(searchText, entity.title) ||
                        textContainsLowerCase(searchText, getEmployeeName(entity.employeeId)) ||
                        textContainsLowerCase(searchText, getEmployeeName(entity.meetingPartnerId)) ||
                        textContainsLowerCase(searchText, entity.status2))
                }
                setSelected={setSelected}
                views={[
                    {
                        id: 'all',
                        title: langStrings.oftenUsed.allEntries,
                        filter()
                        {
                            return true;
                        },
                    },
                    {
                        id: 'my',
                        title: langStrings.oftenUsed.myEntries,
                        filter(entity)
                        {
                            return entity.employeeId === emp?.id;
                        },
                    },
                ]}
                types={types}
                actions={actions}
            />
        </>
    );
}
